/**
 * «Importo staf» — magjistari me tre hapa.
 *
 * Klienti vjen nga një sistem tjetër me një fletë Excel të shkruar për atë
 * sistem. Ky ekran e pranon ashtu siç është dhe e përkthen: shkarko shabllonin
 * (nëse s'ka listë fare) → ngarko → **shih dhe rregullo hartën e kolonave** →
 * shih ç'do të ndodhë me çdo rresht → importo.
 *
 * Hapi i hartës është arsyeja pse ky ekran ekziston. Njohja automatike i gjen
 * shumicën e kolonave («Anual Leave» → «Pushimi vjetor»), por jo të gjitha, dhe
 * një hamendje e gabuar do të fuste mbiemrat te pozita. Prandaj harta shfaqet
 * gjithmonë, edhe kur çdo kolonë u njoh — njeriu e konfirmon, sistemi nuk
 * vendos vetë.
 *
 * Gjithë logjika e leximit, e validimit dhe e shabllonit rri te
 * `utils/staffImport.js` (e testueshme pa React). Këtu ka vetëm ekran.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel, IconButton, LinearProgress,
  MenuItem, Paper, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import apiService from '../../services/apiServices';
import {
  IMPORT_COLUMNS, SECTIONS, detectMapping, unmappedHeaders, readWorkbook,
  validateRows, toPayload, summarize, downloadTemplate, downloadErrorReport,
} from '../../utils/staffImport';

const ACCEPT = '.xlsx,.xls,.csv';
// Një import i vetëm nuk duhet të bllokojë as serverin as shfletuesin: mbi këtë
// numër rreshtash lista ndahet vetë në pjesë dhe dërgohet me radhë.
const CHUNK = 50;

const nz = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

export default function StaffImportDialog({ open, onClose, onImported, departments = [], branches = [], workers = [] }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const fileRef = useRef(null);

  const [step, setStep] = useState('source');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState({ headers: [], rows: [], sheetName: '', sheetNames: [] });
  const [raw, setRaw] = useState(null);          // ArrayBuffer, që fleta të ndërrohet pa ringarkuar
  const [mapping, setMapping] = useState({});
  const [readError, setReadError] = useState('');
  const [withContracts, setWithContracts] = useState(true);
  const [withBalances, setWithBalances] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  const tt = useCallback(
    (k, fallback, opts) => t(`worker.import.${k}`, { defaultValue: fallback, ...(opts || {}) }),
    [t],
  );

  const reset = useCallback(() => {
    setStep('source'); setFileName(''); setParsed({ headers: [], rows: [], sheetName: '', sheetNames: [] });
    setRaw(null); setMapping({}); setReadError(''); setBusy(false); setProgress(0); setResult(null);
  }, []);

  const close = useCallback(() => { if (!busy) { reset(); onClose?.(); } }, [busy, reset, onClose]);

  // ── Hapi 1: leximi ────────────────────────────────────────────────────────
  const ingest = useCallback((buffer, sheet) => {
    try {
      const p = readWorkbook(buffer, sheet);
      if (!p.headers.length) {
        setReadError(tt('errNoHeaders', 'Skedari nuk ka rresht me emra kolonash. Sigurohu që rreshti i parë i mbushur i përmban titujt.'));
        return;
      }
      setParsed(p);
      setMapping(detectMapping(p.headers));
      setReadError('');
      setStep('map');
    } catch (e) {
      setReadError(tt('errUnreadable', 'Skedari nuk u lexua dot. Pranohen .xlsx, .xls ose .csv.'));
    }
  }, [tt]);

  const onFile = useCallback(async (file) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      setRaw(buf);
      ingest(buf);
    } catch (e) {
      setReadError(tt('errUnreadable', 'Skedari nuk u lexua dot. Pranohen .xlsx, .xls ose .csv.'));
    }
  }, [ingest, tt]);

  // ── Validimi (rillogaritet sa herë ndryshon harta) ────────────────────────
  const rows = useMemo(() => {
    if (!parsed.rows.length) return [];
    return validateRows(parsed.rows, mapping, { departments, branches, workers });
  }, [parsed.rows, mapping, departments, branches, workers]);

  const stats = useMemo(() => summarize(rows), [rows]);
  const extras = useMemo(() => unmappedHeaders(parsed.headers, mapping), [parsed.headers, mapping]);
  const missingRequired = useMemo(
    () => IMPORT_COLUMNS.filter((c) => c.required && (mapping[c.key] ?? -1) < 0),
    [mapping],
  );
  const sectioned = useMemo(
    () => SECTIONS
      .map((section) => ({ section, columns: IMPORT_COLUMNS.filter((c) => c.section === section.key) }))
      .filter((g) => g.columns.length),
    [],
  );

  const setColumn = (key, idx) => setMapping((m) => {
    const next = { ...m };
    // Një kolonë e skedarit s'mund t'u takojë dy fushave: zgjedhja e re e liron të vjetrën.
    if (idx > -1) Object.keys(next).forEach((k) => { if (next[k] === idx) next[k] = -1; });
    next[key] = idx;
    return next;
  });

  // ── Dërgimi ───────────────────────────────────────────────────────────────
  const runImport = useCallback(async () => {
    const ready = rows.filter((r) => r.ok).map(toPayload);
    if (!ready.length) return;
    setBusy(true); setProgress(0);
    const acc = { created: 0, failed: 0, skipped: 0, contracts: 0, balances: 0, results: [] };
    try {
      for (let i = 0; i < ready.length; i += CHUNK) {
        const slice = ready.slice(i, i + CHUNK);
        // eslint-disable-next-line no-await-in-loop
        const res = await apiService().post('workers/import', {
          rows: slice,
          options: { createContracts: withContracts, createLeaveBalances: withBalances },
        });
        if (!res?.success) throw new Error(res?.error || 'import failed');
        const d = res.data || {};
        acc.created += d.created || 0;
        acc.failed += d.failed || 0;
        acc.skipped += d.skipped || 0;
        acc.contracts += d.contracts || 0;
        acc.balances += d.balances || 0;
        acc.results.push(...(d.results || []));
        setProgress(Math.round(((i + slice.length) / ready.length) * 100));
      }
      setResult(acc);
      setStep('done');
      onImported?.(acc);
    } catch (e) {
      setResult({ ...acc, error: e?.message || String(e) });
      setStep('done');
      if (acc.created) onImported?.(acc);
    } finally {
      setBusy(false);
    }
  }, [rows, withContracts, withBalances, onImported]);

  // ── Pjesë të ekranit ──────────────────────────────────────────────────────
  const StatPill = ({ tone, icon, value, label }) => (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 132 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1, display: 'grid', placeItems: 'center',
        bgcolor: alpha(theme.palette[tone].main, 0.12), color: theme.palette[tone].main,
      }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.secondary' }}>
          {label}
        </Typography>
      </Box>
    </Paper>
  );

  const sourceStep = (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ '& .MuiAlert-message': { py: 0.5 } }}>
        {tt('intro', 'Ngarko listën që ke nga sistemi i mëparshëm. Kolonat njihen vetvetiu dhe i konfirmon në hapin tjetër — nuk ka nevojë ta rishkruash fletën.')}
      </Alert>

      <Paper
        variant="outlined"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer?.files?.[0]); }}
        onClick={() => fileRef.current?.click()}
        sx={{
          p: 4, borderRadius: 2, textAlign: 'center', cursor: 'pointer', borderStyle: 'dashed',
          '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.03) },
        }}
      >
        <UploadFileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
        <Typography sx={{ fontWeight: 700 }}>{tt('dropTitle', 'Lësho skedarin këtu ose kliko për ta zgjedhur')}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {tt('dropHint', 'Excel (.xlsx, .xls) ose CSV')}
        </Typography>
        {fileName && <Chip size="small" label={fileName} sx={{ mt: 1.5 }} />}
      </Paper>
      <input ref={fileRef} type="file" accept={ACCEPT} hidden
             onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />

      {readError && <Alert severity="error">{readError}</Alert>}

      <Divider>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tt('or', 'ose')}</Typography>
      </Divider>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{tt('templateTitle', 'Nuk ke listë të gatshme?')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {tt('templateHint', 'Shkarko shabllonin — ka kolonat e sakta, dy rreshta shembull dhe emrat e departamenteve që ekzistojnë te sistemi.')}
          </Typography>
        </Box>
        <Button
          variant="outlined" startIcon={<DownloadIcon />}
          onClick={() => downloadTemplate({ departments, branches })}
          sx={{ borderRadius: 1.5, flexShrink: 0 }}
        >
          {tt('downloadTemplate', 'Shkarko shabllonin')}
        </Button>
      </Stack>
    </Stack>
  );

  const mapStep = (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
        <Chip size="small" label={`${fileName}${parsed.sheetName ? ` · ${parsed.sheetName}` : ''}`} />
        <Chip size="small" color="primary" variant="outlined" label={tt('rowsFound', '{{n}} rreshta', { n: parsed.rows.length })} />
        {parsed.sheetNames.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={parsed.sheetName}
              onChange={(e) => ingest(raw, e.target.value)}
              sx={{ height: 30, fontSize: '0.8125rem' }}
            >
              {parsed.sheetNames.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Stack>

      {missingRequired.length > 0 && (
        <Alert severity="warning">
          {tt('missingRequired', "Këto kolona të detyrueshme s'u lidhën ende: {{cols}}", {
            cols: missingRequired.map((c) => c.label).join(', '),
          })}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 340 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{tt('colField', 'Fusha te Nexuma')}</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: '38%' }}>{tt('colSource', 'Kolona te skedari yt')}</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{tt('colSample', 'Shembull nga rreshti i parë')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sectioned.map(({ section, columns }) => [
              // Kreu i seksionit: dyzet rreshta pa ndarje janë listë, jo formë.
              // Numri i lidhur përbri i tregon njeriut ku ka mbetur bosh pa i
              // lexuar të gjitha rreshtat një nga një.
              <TableRow key={`sec-${section.key}`}>
                <TableCell colSpan={3} sx={{
                  py: 0.5, borderBottom: 'none',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }}>
                  <Typography sx={{
                    fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.06em',
                    textTransform: 'uppercase', color: 'primary.main',
                  }}>
                    {section.label}
                    <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontWeight: 600, letterSpacing: 0 }}>
                      {columns.filter((c) => (mapping[c.key] ?? -1) > -1).length}/{columns.length}
                    </Box>
                  </Typography>
                </TableCell>
              </TableRow>,
              ...columns.map((c) => {
              const idx = mapping[c.key] ?? -1;
              const sample = idx > -1 && parsed.rows[0] ? parsed.rows[0][idx] : '';
              return (
                <TableRow key={c.key} hover>
                  <TableCell sx={{ fontSize: '0.8125rem', pl: 2.5 }}>
                    {c.label}
                    {c.required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small" fullWidth value={idx}
                      onChange={(e) => setColumn(c.key, Number(e.target.value))}
                      sx={{ height: 30, fontSize: '0.8125rem' }}
                    >
                      <MenuItem value={-1}><em>{tt('skip', '— mos e importo —')}</em></MenuItem>
                      {parsed.headers.map((h, i) => (
                        <MenuItem key={`${h}-${i}`} value={i}>{h || `(${tt('colNo', 'kolona')} ${i + 1})`}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {sample instanceof Date ? sample.toISOString().slice(0, 10) : nz(sample)}
                  </TableCell>
                </TableRow>
              );
            })])}
          </TableBody>
        </Table>
      </TableContainer>

      {extras.length > 0 && (
        <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { py: 0.5 } }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {tt('ignored', 'Këto kolona të skedarit nuk lidhen me asnjë fushë dhe do të anashkalohen:')}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
            {extras.map((e) => <Chip key={e.index} size="small" variant="outlined" label={e.header} />)}
          </Stack>
        </Alert>
      )}
    </Stack>
  );

  const previewStep = (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1.25}>
        <StatPill tone="success" icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} value={stats.ready} label={tt('statReady', 'gati')} />
        <StatPill tone="warning" icon={<WarningAmberIcon sx={{ fontSize: 18 }} />} value={stats.warned} label={tt('statWarned', 'me vërejtje')} />
        <StatPill tone="error" icon={<ErrorOutlineIcon sx={{ fontSize: 18 }} />} value={stats.blocked} label={tt('statBlocked', 'nuk hyjnë')} />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControlLabel
          control={<Switch size="small" checked={withContracts} onChange={(e) => setWithContracts(e.target.checked)} />}
          label={<Typography variant="body2">{tt('optContracts', 'Krijo edhe kontratën fillestare ({{n}} rreshta)', { n: stats.contracts })}</Typography>}
        />
        <FormControlLabel
          control={<Switch size="small" checked={withBalances} onChange={(e) => setWithBalances(e.target.checked)} />}
          label={<Typography variant="body2">{tt('optBalances', 'Vendos bilancin hapës të pushimeve ({{n}} rreshta)', { n: stats.balances })}</Typography>}
        />
      </Stack>

      {stats.blocked > 0 && (
        <Alert severity="warning">
          {tt('blockedNote', '{{n}} rreshta kanë gabim dhe nuk do të dërgohen. Të tjerët importohen normalisht — pastaj shkarko raportin, rregulloji dhe ngarkoji sërish.', { n: stats.blocked })}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['#', tt('thName', 'Punëtori'), tt('thContact', 'Kontakti'), tt('thOrg', 'Vendi i punës'), tt('thStart', 'Fillimi'), tt('thPay', 'Paga'), tt('thIssues', 'Vërejtje')].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const v = r.values;
              const issues = [...r.errors, ...r.warnings];
              const tone = !r.ok ? 'error' : r.warnings.length ? 'warning' : null;
              return (
                <TableRow key={r.index} sx={tone ? { bgcolor: alpha(theme.palette[tone].main, 0.05) } : undefined}>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{r.index + 2}</TableCell>
                  <TableCell sx={{ fontSize: '0.8125rem' }}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                      {`${v.FirstName || ''} ${v.LastName || ''}`.trim() || '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{nz(v.PersonalNumber)}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    <div>{nz(v.Email)}</div>
                    <div>{nz(v.Phone)}</div>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    <div>{nz(v.Position)}</div>
                    <div>{nz(v.DepartmentText)}</div>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{nz(v.HireDate)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {/* Baza rri PRANË shifrës, jo te një kolonë tjetër: «650»
                        vetëm nuk thotë asgjë derisa të dish nëse është bruto. */}
                    <div>{v.Salary === null || v.Salary === undefined ? '—'
                      : `${v.Salary} € ${v.SalaryType === 'net' ? tt('net', 'neto') : tt('gross', 'bruto')}`}</div>
                    <div>{v.Residency === 'foreign' ? tt('foreign', 'i huaj') : ''}</div>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.72rem' }}>
                    {issues.length === 0
                      ? <Typography variant="caption" sx={{ color: 'success.main' }}>{tt('rowOk', 'Në rregull')}</Typography>
                      : (
                        <Stack spacing={0.25}>
                          {issues.map((e, i) => (
                            <Typography key={i} variant="caption"
                              sx={{ color: r.errors.includes(e) ? 'error.main' : 'warning.onSoft' }}>
                              {e.message}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {busy && (
        <Box>
          <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {tt('importing', 'Duke importuar… {{p}}%', { p: progress })}
          </Typography>
        </Box>
      )}
    </Stack>
  );

  const failedRows = result?.results?.filter((r) => r.status === 'failed') || [];
  const doneStep = (
    <Stack spacing={2}>
      {result?.error
        ? <Alert severity="error">{tt('importError', 'Importi u ndërpre: {{msg}}', { msg: result.error })}</Alert>
        : <Alert severity="success">{tt('importDone', 'Importi përfundoi.')}</Alert>}

      <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1.25}>
        <StatPill tone="success" icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} value={result?.created || 0} label={tt('statCreated', 'punëtorë')} />
        <StatPill tone="info" icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} value={result?.contracts || 0} label={tt('statContracts', 'kontrata')} />
        <StatPill tone="info" icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} value={result?.balances || 0} label={tt('statBalances', 'bilance')} />
        <StatPill tone="error" icon={<ErrorOutlineIcon sx={{ fontSize: 18 }} />} value={result?.failed || 0} label={tt('statFailed', 'dështuan')} />
      </Stack>

      {failedRows.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 220 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{tt('thReason', 'Arsyeja')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {failedRows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{r.row}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'error.main' }}>{r.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(stats.blocked > 0 || stats.warned > 0) && (
        <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ alignSelf: 'flex-start', borderRadius: 1.5 }}
                onClick={() => downloadErrorReport(rows, parsed.headers)}>
          {tt('downloadErrors', 'Shkarko rreshtat me problem')}
        </Button>
      )}
    </Stack>
  );

  const canNext = step === 'map' && missingRequired.length === 0 && rows.length > 0;

  return (
    <Dialog open={open} onClose={close} maxWidth="lg" fullWidth
            PaperProps={{ sx: { borderRadius: 2, minWidth: 0 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5, display: 'grid', placeItems: 'center', flexShrink: 0,
          bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main',
        }}>
          <GroupAddIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
            {tt('title', 'Importo staf')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
            {step === 'source' ? tt('stepSource', 'Hapi 1 nga 3 · zgjidh skedarin')
              : step === 'map' ? tt('stepMap', 'Hapi 2 nga 3 · konfirmo kolonat')
              : step === 'preview' ? tt('stepPreview', "Hapi 3 nga 3 · shiko ç'do të ndodhë")
              : tt('stepDone', 'Përfundoi')}
          </Typography>
        </Box>
        <IconButton onClick={close} size="small" disabled={busy}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minWidth: 0 }}>
        {step === 'source' && sourceStep}
        {step === 'map' && mapStep}
        {step === 'preview' && previewStep}
        {step === 'done' && doneStep}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {(step === 'map' || step === 'preview') && (
          <Button onClick={() => setStep(step === 'map' ? 'source' : 'map')}
                  startIcon={<ArrowBackIcon sx={{ fontSize: '13px !important' }} />} disabled={busy}>
            {tt('back', 'Prapa')}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {step === 'done'
          ? <Button variant="contained" onClick={close} sx={{ borderRadius: 1.5 }}>{t('common.close', 'Mbyll')}</Button>
          : <Button onClick={close} disabled={busy}>{t('common.cancel', 'Anulo')}</Button>}
        {step === 'map' && (
          <Button variant="contained" disabled={!canNext} onClick={() => setStep('preview')} sx={{ borderRadius: 1.5 }}>
            {tt('next', 'Vazhdo')}
          </Button>
        )}
        {step === 'preview' && (
          <Button
            variant="contained" onClick={runImport} sx={{ borderRadius: 1.5 }}
            disabled={busy || stats.total - stats.blocked === 0}
            startIcon={busy ? <CircularProgress size={15} color="inherit" /> : <GroupAddIcon sx={{ fontSize: '17px !important' }} />}
          >
            {tt('doImport', 'Importo {{n}} punëtorë', { n: stats.total - stats.blocked })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
